/**
 * [PORTABLE] Rich Modal for Selfie Levels - Gamified & Glassmorphism Design
 * Target path in main project: src/features/onboarding/components/SelfieLevelDialog.tsx
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Sparkles, X, Lightbulb, Image as ImageIcon, ArrowRight, Rocket, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import mixpanel from "mixpanel-browser";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

import { selfieGridItems, SELFIE_LEVELS } from "../data/selfie-grid";
import { useOnboardingUiStore } from "../store/onboarding-ui-store";
import { useGenerateAndPoll } from "../hooks/use-onboarding-mutations";
import { libraryItemUrl } from "../utils/media-url";
import { useOnboarding } from "../hooks/use-onboarding";
import { BadgesStrip } from "./BadgesStrip";

interface SelfieLevelDialogProps {
  resultUrlByLevelId: Record<string, string | undefined>;
  completedCount: number;
  creditsRemaining: number;
  creditsTotal: number;
}

export function SelfieLevelDialog({
  resultUrlByLevelId,
  completedCount,
  creditsRemaining,
  creditsTotal,
}: SelfieLevelDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const selectedLevelId = useOnboardingUiStore((s) => s.selectedLevelId);
  const isOpen = useOnboardingUiStore((s) => s.isPromptModalOpen);
  const closePromptModal = useOnboardingUiStore((s) => s.closePromptModal);
  const openPromptModal = useOnboardingUiStore((s) => s.openPromptModal);
  const selfieLibraryItemId = useOnboardingUiStore((s) => s.selectedSelfieLibraryItemId);

  const { progress, skipOnboarding, isSkipping } = useOnboarding();
  const { start, isPolling, isFailed, errorMessage } = useGenerateAndPoll("selfie");

  const item = selfieGridItems.find((i) => i.levelId === selectedLevelId) ?? null;
  const inputSelfieUrl = libraryItemUrl(progress?.selfie?.uploadedLibraryItemId);

  const resultRef = useRef<HTMLDivElement>(null);

  // Oto-Scroll
  useEffect(() => {
    if (item && (resultUrlByLevelId[item.levelId] || isFailed)) {
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 300);
    }
  }, [resultUrlByLevelId, item, isFailed]);

  const handleClose = () => {
    try {
      const isLevelCompleted = !!resultUrlByLevelId[item?.levelId || ""];
      mixpanel.track("onboarding_modal_closed", {
        level_id: selectedLevelId,
        abandoned: !isLevelCompleted && !isPolling
      });
    } catch (e) {
      console.warn("Mixpanel error:", e);
    }
    closePromptModal();
  };

  const handleStartAdventure = () => {
    skipOnboarding(
      { onSuccess: () => { navigate("/", { replace: true }); } },
      false
    );
  };

  if (!isOpen) return null;

  // 🌟 ÖZEL DURUM: BÜYÜK FİNAL MODALI
  if (selectedLevelId === "FINAL") {
    const displayBadges = (progress?.badges ?? []).filter(b => b.type === "selfie_complete");
    return (
      <div className="fixed inset-0 z-100 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-300">
        <div className="bg-bg-primary w-full max-w-lg rounded-4xl shadow-2xl border border-border-primary relative flex flex-col items-center text-center overflow-hidden animate-in zoom-in-95 duration-500 p-8 sm:p-12">
          <button onClick={handleClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-bg-tertiary transition-colors text-text-secondary z-20">
            <X className="h-6 w-6" />
          </button>
          
          <div className="absolute -top-16 -left-16 w-48 h-48 bg-fuchsia-500/20 rounded-full blur-3xl mix-blend-multiply" />
          <div className="absolute -bottom-16 -right-16 w-48 h-48 bg-cyan-500/20 rounded-full blur-3xl mix-blend-multiply" />
          
          <div className="relative z-10 inline-flex p-4 rounded-full bg-linear-to-br from-fuchsia-500 to-cyan-500 text-white mb-6 shadow-xl shadow-fuchsia-500/20">
            <Rocket className="h-8 w-8" />
          </div>
          <h4 className="relative z-10 text-2xl sm:text-3xl font-extrabold text-text-primary mb-3">
            {t("onboarding.allCompletedTitle", { defaultValue: "Müthiş! Tüm Dönüşümler Hazır" })}
          </h4>
          <p className="relative z-10 text-text-secondary text-sm sm:text-base max-w-md mx-auto mb-8 leading-relaxed">
            {t("onboarding.allCompletedDesc", { defaultValue: "Kendi AI stilini keşfettin. Şimdi Harmony AI dünyasında gerçek bir maceraya atılmaya hazırsın!" })}
          </p>
          <div className="relative z-10 flex flex-col items-center gap-6 w-full">
            <BadgesStrip badges={displayBadges} />
            <Button variant="darkPrimary" size="lg" className="w-full sm:w-auto px-12 py-7 text-lg rounded-2xl shadow-xl hover:scale-105 transition-transform" onClick={handleStartAdventure} disabled={isSkipping}>
              {t("onboarding.startAdventure", { defaultValue: "Maceraya Başla" })}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // --- NORMAL LEVEL MODALLARI (S1, S2, S3) ---
  if (!item) return null;

  const isLevelCompleted = !!resultUrlByLevelId[item.levelId];
  const currentIndex = selfieGridItems.findIndex(i => i.levelId === item.levelId);
  const isLastLevel = currentIndex === selfieGridItems.length - 1;
  const nextLevelId = isLastLevel ? "FINAL" : selfieGridItems[currentIndex + 1]?.levelId;

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-md animate-in fade-in duration-300">
      
      {/* 🚀 ANA KUTU (Taşmaları engelleyen kusursuz Flex yapısı) */}
      <div className="bg-bg-primary w-full max-w-200 h-full sm:h-auto sm:max-h-[90dvh] sm:rounded-4xl shadow-2xl border-x sm:border border-border-primary relative flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
        
        {/* 🚀 SMOOTH TRANSITION WRAPPER */}
        <div key={item.levelId} className="flex flex-col flex-1 h-full overflow-hidden animate-in fade-in slide-in-from-right-8 duration-500 ease-out">
          
          {/* MODAL HEADER (Sabit/Shrink-0) */}
          <div className="shrink-0 p-6 border-b border-border-primary flex items-center justify-between bg-bg-primary z-20 shadow-[0_4px_20px_-10px_rgba(0,0,0,0.05)]">
            <div className="flex items-center gap-4">
              {/* 🚀 DÜZELTME: Emoji arka planı silindi, sade ve doğal bırakıldı */}
              <div className="flex items-center justify-center text-3xl sm:text-4xl pr-1">
                {item.emoji}
              </div>
              <div>
                <h2 className="text-xl font-extrabold text-text-primary leading-tight">
                  L{item.level} — {t(`onboarding.levels.${item.levelId}.label`)}
                </h2>
                <p className="text-[10px] uppercase tracking-widest font-bold text-purple-600 dark:text-purple-400 mt-0.5">
                  {t(`onboarding.levels.${item.levelId}.focus`)}
                </p>
              </div>
            </div>
            <button onClick={handleClose} className="p-2 rounded-full hover:bg-bg-secondary transition-colors text-text-tertiary hover:text-text-primary">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* KAYDIRILABİLİR İÇERİK (Sadece bu alan scroll olur, footer ve header taş gibi sabit kalır) */}
          <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-10 pb-32 sm:pb-8 bg-bg-primary/50">
            
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
              
              {/* 1. Original Input Card */}
              {inputSelfieUrl && (
                <div className="md:col-span-4 space-y-2">
                  <span className="text-[10px] font-bold text-text-tertiary uppercase tracking-widest ml-1 flex items-center gap-1.5">
                    <ImageIcon className="h-3 w-3" /> {t("onboarding.inputSelfie", { defaultValue: "Girdi" })}
                  </span>
                  <div className="aspect-square rounded-2xl overflow-hidden border-2 border-border-primary shadow-sm bg-bg-secondary">
                    <img src={inputSelfieUrl} alt="Input" className="w-full h-full object-cover" />
                  </div>
                </div>
              )}

              {/* 2. AI Magic Prompt Card */}
              <div className={cn("space-y-2", inputSelfieUrl ? "md:col-span-8" : "md:col-span-12")}>
                <span className="text-[10px] font-bold text-text-tertiary uppercase tracking-widest ml-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> {t("onboarding.promptLabel", { defaultValue: "Yapay Zeka Komutu" })}
                </span>
                <div className="bg-bg-secondary/50 rounded-3xl p-5 md:p-6 border border-purple-500/10 relative overflow-hidden group transition-all hover:border-purple-500/30 shadow-sm">
                  <div className="absolute -right-4 -top-4 w-24 h-24 bg-linear-to-br from-fuchsia-500/10 to-purple-600/10 rounded-full blur-2xl"></div>
                  <p className="text-text-primary text-sm italic leading-relaxed relative z-10 font-medium">
                    "{t(`onboarding.levels.${item.levelId}.prompt`)}"
                  </p>
                </div>
              </div>
            </div>

            {/* KREDİ VE İLERLEME */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-bg-secondary p-5 rounded-2xl flex items-center justify-between border border-border-primary shadow-sm">
                <div>
                  <p className="text-[10px] font-bold text-text-tertiary uppercase tracking-widest">{t("onboarding.creditsRemaining", { defaultValue: "Kalan Kredi" })}</p>
                  <p className="text-lg font-extrabold text-text-primary mt-0.5">
                    {creditsRemaining} <span className="text-sm font-normal text-text-tertiary">/ {creditsTotal}</span>
                  </p>
                </div>
                <div className="w-10 h-10 bg-linear-to-br from-fuchsia-500/10 to-purple-600/10 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-400">
                  <Sparkles className="w-5 h-5" />
                </div>
              </div>

              <div className="bg-bg-secondary p-5 rounded-2xl flex flex-col justify-center border border-border-primary shadow-sm">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-[10px] font-bold text-text-tertiary uppercase tracking-widest">{t("onboarding.progress", { defaultValue: "İlerleme" })}</p>
                  <span className="text-xs font-black text-purple-600 dark:text-purple-400">%{Math.min(100, Math.round((completedCount / SELFIE_LEVELS) * 100))}</span>
                </div>
                <Progress 
                  value={Math.min(100, (completedCount / SELFIE_LEVELS) * 100)} 
                  className="h-2.5 bg-bg-tertiary [&>div]:bg-linear-to-r [&>div]:from-fuchsia-500 [&>div]:to-purple-600 shadow-inner" 
                />
              </div>
            </div>

            {/* DÖNÜŞÜM SONUCU */}
            <div className="space-y-6 pt-2" ref={resultRef}>
              
              {isFailed && (
                <div className="p-5 rounded-2xl bg-red-500/10 border border-red-500/20 flex gap-4 animate-in zoom-in-95 duration-300">
                  <div className="mt-0.5"><AlertCircle className="h-5 w-5 text-red-500" /></div>
                  <div className="space-y-1 text-red-500">
                    <p className="text-sm font-bold">{t("onboarding.generationFailed", { defaultValue: "Üretim Başarısız" })}</p>
                    <p className="text-sm opacity-90 leading-relaxed">{errorMessage || t("onboarding.errors.failed")}</p>
                  </div>
                </div>
              )}

              {isLevelCompleted ? (
                <div className="flex flex-col items-center space-y-6 animate-in fade-in slide-in-from-bottom-8 duration-700 w-full">
                  
                  <div className="relative flex flex-col items-center w-full">
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-linear-to-r from-fuchsia-500 to-purple-600 text-white text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-[0.2em] z-10 shadow-lg">
                      {t("onboarding.resultSection", { defaultValue: "Dönüşüm Sonucu" })}
                    </div>
                    
                    <div className="w-full max-w-md aspect-square bg-bg-secondary rounded-4xl overflow-hidden border-4 border-bg-primary shadow-[0_0_30px_rgba(168,85,247,0.15)] relative">
                      <img src={resultUrlByLevelId[item.levelId]} alt="Result" className="w-full h-full object-cover" />
                    </div>
                  </div>

                  {/* Hint / İpucu Alanı - Footer'a girmesi tamamen engellendi */}
                  <div className="flex items-start gap-3 bg-purple-500/5 p-5 rounded-3xl border border-purple-500/10 w-full max-w-md shadow-sm">
                    <div className="text-purple-600 dark:text-purple-400 shrink-0">
                      <Lightbulb className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-text-primary mb-1">{t("onboarding.hintTitle", { defaultValue: "Bunu Nasıl Başardık?" })}</h4>
                      <p className="text-xs text-text-secondary leading-relaxed">
                        {t(`onboarding.levels.${item.levelId}.hint`)}
                      </p>
                    </div>
                  </div>

                </div>
              ) : (
                !isFailed && (
                  <div className="relative flex flex-col items-center w-full opacity-50">
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-bg-tertiary text-text-tertiary text-[10px] font-black px-4 py-1 rounded-full uppercase tracking-[0.2em] z-10">
                      {t("onboarding.waitingForGeneration", { defaultValue: "Awaiting Magic" })}
                    </div>
                    <div className="w-full max-w-md aspect-square rounded-4xl border-2 border-dashed border-border-primary flex flex-col items-center justify-center text-text-tertiary bg-bg-secondary/20">
                      {isPolling ? (
                         <Loader2 className="h-10 w-10 mb-3 animate-spin text-purple-500" />
                      ) : (
                         <Sparkles className="h-10 w-10 mb-3" />
                      )}
                      <p className="text-sm font-medium">
                        {isPolling ? t("onboarding.generating") : t("onboarding.generateToSee")}
                      </p>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>

          {/* 🚀 MODAL FOOTER (Sabit ve Korumalı - Asla içeriğe karışmaz) */}
          <div className="shrink-0 p-6 bg-bg-secondary/50 border-t border-border-primary flex items-center justify-between gap-4 z-20">
            <button onClick={handleClose} className="px-6 py-3.5 text-sm font-bold text-text-tertiary hover:text-text-primary transition-colors">
              {t("onboarding.close", { defaultValue: "Kapat" })}
            </button>

            {/* AKILLI BUTON: Duruma göre Generate veya Next oluyor! */}
            {isLevelCompleted ? (
              <Button
                variant="darkPrimary"
                className="px-8 py-7 rounded-full font-bold text-sm shadow-xl shadow-purple-500/20 flex items-center gap-3 group transition-all hover:scale-[1.02] active:scale-[0.98] animate-in slide-in-from-right-8 duration-500"
                onClick={() => openPromptModal(nextLevelId)}
              >
                {isLastLevel ? t("onboarding.seeFinal", { defaultValue: "Finali Gör" }) : t("onboarding.nextStep", { defaultValue: "Sonraki Adıma Geç" })}
                <ArrowRight className="h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
              </Button>
            ) : (
              <Button
                variant="darkPrimary"
                disabled={isPolling || isLevelCompleted} 
                className="px-8 py-7 rounded-full font-bold text-sm shadow-xl shadow-purple-500/20 flex items-center gap-3 group transition-all hover:scale-[1.02] active:scale-[0.98] animate-in fade-in duration-500"
                onClick={() => {
                  try { mixpanel.track("onboarding_action_started", { action_type: "generate", level_id: item.levelId }); } catch (e) {}
                  start({
                    levelId: item.levelId,
                    ...(selfieLibraryItemId ? { selfieLibraryItemId } : {}),
                  });
                }}
              >
                {isPolling ? (
                  <><Loader2 className="h-5 w-5 animate-spin" /> {t("onboarding.generating")}</>
                ) : (
                  <><Sparkles className="h-5 w-5 text-yellow-400 fill-yellow-400 group-hover:rotate-12 transition-transform" /> {t("onboarding.generateButton")}</>
                )}
              </Button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}